/**
 * Vuex plugin for memory optimization
 * Prevents memory leaks and optimizes store performance
 */

const MAX_ITEMS = {
  licenses: 1000,
  auditLogs: 500,
  errors: 100,
  notifications: 50
};

const MAX_AGE = {
  licenses: 60 * 60 * 1000, // 1 hour
  auditLogs: 30 * 60 * 1000, // 30 minutes
  errors: 15 * 60 * 1000, // 15 minutes
  notifications: 5 * 60 * 1000 // 5 minutes
};

export const memoryOptimizer = (store) => {
  let cleanupTimer = null;

  // Track large data sets
  const trackedMutations = new Map([
    ['licenses/SET_LICENSES', 'licenses'],
    ['audit/ADD_LOG', 'auditLogs'],
    ['errors/ADD_ERROR', 'errors'],
    ['notifications/ADD_NOTIFICATION', 'notifications']
  ]);

  // Clean old data periodically
  const cleanupOldData = () => {
    const now = Date.now();

    // Clean licenses
    if (store.state.licenses?.licenses?.length > MAX_ITEMS.licenses) {
      const licenses = store.state.licenses.licenses.slice(-MAX_ITEMS.licenses);
      store.commit('licenses/SET_LICENSES', licenses);
    }

    // Clean old cached data
    Object.keys(store.state).forEach(module => {
      if (store.state[module]?.cache) {
        Object.keys(store.state[module].cache).forEach(key => {
          const item = store.state[module].cache[key];
          if (item?.timestamp) {
            const age = now - item.timestamp;
            if (age > MAX_AGE[module] || age > 60 * 60 * 1000) {
              delete store.state[module].cache[key];
            }
          }
        });
      }
    });

    // Clear empty objects
    cleanEmptyObjects(store.state);
  };

  // Remove empty nested objects to free memory
  const cleanEmptyObjects = (obj) => {
    Object.keys(obj).forEach(key => {
      if (obj[key] && typeof obj[key] === 'object') {
        if (Array.isArray(obj[key]) && obj[key].length === 0) {
          delete obj[key];
        } else if (Object.keys(obj[key]).length === 0) {
          delete obj[key];
        } else {
          cleanEmptyObjects(obj[key]);
        }
      }
    });
  };

  // Monitor mutations for memory leaks
  store.subscribe((mutation, state) => {
    const dataType = trackedMutations.get(mutation.type);

    if (dataType && state[dataType]) {
      const data = Array.isArray(state[dataType]) ? state[dataType] : state[dataType][dataType];

      if (Array.isArray(data) && data.length > MAX_ITEMS[dataType]) {
        console.warn(`Warning: ${dataType} exceeds ${MAX_ITEMS[dataType]} items. Consider pagination.`);

        // Automatically trim if too large
        if (data.length > MAX_ITEMS[dataType] * 2) {
          const trimmed = data.slice(-MAX_ITEMS[dataType]);
          store.commit(`${dataType}/SET_${dataType.toUpperCase()}`, trimmed);
        }
      }
    }
  });

  // Set up periodic cleanup
  const startCleanup = () => {
    stopCleanup();
    cleanupTimer = setInterval(cleanupOldData, 5 * 60 * 1000); // Every 5 minutes
  };

  const stopCleanup = () => {
    if (cleanupTimer) {
      clearInterval(cleanupTimer);
      cleanupTimer = null;
    }
  };

  // Start cleanup when store is created
  startCleanup();

  // Clean up on page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      stopCleanup();
      // Clear large data sets on unload
      store.commit('licenses/SET_LICENSES', []);
      if (store.state.audit) {
        store.commit('audit/CLEAR_LOGS');
      }
    });
  }

  // Expose cleanup methods to store
  store.memoryOptimizer = {
    cleanup: cleanupOldData,
    startCleanup,
    stopCleanup,
    getMemoryUsage: () => {
      const usage = {};
      Object.keys(MAX_ITEMS).forEach(key => {
        const state = store.state[key];
        if (state) {
          const data = Array.isArray(state) ? state : state[key];
          if (Array.isArray(data)) {
            usage[key] = {
              count: data.length,
              max: MAX_ITEMS[key],
              percentage: (data.length / MAX_ITEMS[key]) * 100
            };
          }
        }
      });
      return usage;
    }
  };
};

/**
 * Vuex module for managing memory optimization state
 */
export const memoryModule = {
  namespaced: true,

  state: () => ({
    isOptimizationEnabled: true,
    memoryWarnings: [],
    lastCleanup: null
  }),

  mutations: {
    SET_OPTIMIZATION_ENABLED(state, enabled) {
      state.isOptimizationEnabled = enabled;
    },

    ADD_MEMORY_WARNING(state, warning) {
      state.memoryWarnings.push({
        ...warning,
        timestamp: Date.now()
      });

      // Keep only recent warnings
      if (state.memoryWarnings.length > 10) {
        state.memoryWarnings = state.memoryWarnings.slice(-10);
      }
    },

    SET_LAST_CLEANUP(state, timestamp) {
      state.lastCleanup = timestamp;
    },

    CLEAR_WARNINGS(state) {
      state.memoryWarnings = [];
    }
  },

  actions: {
    enableOptimization({ commit }) {
      commit('SET_OPTIMIZATION_ENABLED', true);
    },

    disableOptimization({ commit }) {
      commit('SET_OPTIMIZATION_ENABLED', false);
    },

    recordWarning({ commit }, warning) {
      commit('ADD_MEMORY_WARNING', warning);
    },

    performCleanup({ commit, rootState }) {
      // Trigger manual cleanup
      if (rootState.memoryOptimizer) {
        rootState.memoryOptimizer.cleanup();
        commit('SET_LAST_CLEANUP', Date.now());
      }
    }
  },

  getters: {
    isEnabled: state => state.isOptimizationEnabled,
    recentWarnings: state => state.memoryWarnings.slice(-5),
    timeSinceCleanup: state => {
      if (!state.lastCleanup) return null;
      return Date.now() - state.lastCleanup;
    }
  }
};